import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import SocialLeadServices from "../../UserServices/SocialLeadServices";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import "./SocialMediaDashboard.css";

const num = (v) => Number(v || 0).toLocaleString("en-PK");

const Kpi = ({ label, value, sub, tone, icon }) => (
  <div className={`sm-kpi sm-tone-${tone}`}>
    <div className="sm-kpi-body">
      <p className="sm-kpi-label">{label}</p>
      <h4 className="sm-kpi-value">{value}</h4>
      {sub ? <p className="sm-kpi-sub">{sub}</p> : null}
    </div>
    <div className="sm-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

const branchLabel = (branch) => {
  if (branch === "all" || !branch) return "All Branches (F-11 + G-13)";
  if (String(branch) === "15") return "F-11";
  if (String(branch) === "1") return "G-13";
  return "Branch";
};

const emptyStats = () => ({
  total_leads: 0,
  interested: 0,
  not_interested: 0,
  visit_scheduled: 0,
  visit_completed: 0,
  payment_received: 0,
  converted: 0,
  conversion_rate: 0,
  whatsapp_detail: 0,
  first_call_answered: 0,
  call_not_answered: 0,
  whatsapp_message_dropped: 0,
  by_contact_medium: {},
  by_offer: [],
});

export default function SocialMediaDashboard() {
  const { state } = useContext(NoteState);
  const { first_name, last_name, branch_id, role } = state;
  const { branch, setBranch } = useDashboardBranch(role, branch_id);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [today, setToday] = useState(null);
  const [mtd, setMtd] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const branchParams = useCallback(() => {
    if (branch && branch !== "all") return { branch_id: branch };
    return {};
  }, [branch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const monthStart = moment(date).startOf("month").format("YYYY-MM-DD");
    const monthEnd = moment(date).endOf("month").format("YYYY-MM-DD");
    const base = branchParams();

    try {
      const [todayRes, mtdRes, leadsRes] = await Promise.all([
        SocialLeadServices.getStats({ ...base, date }),
        SocialLeadServices.getStats({
          ...base,
          from_date: monthStart,
          to_date: monthEnd,
        }),
        SocialLeadServices.getLeads({
          ...base,
          date,
          per_page: 12,
        }),
      ]);

      const todayData = todayRes?.data?.data;
      const mtdData = mtdRes?.data?.data;
      const leadsPage = leadsRes?.data?.data;

      setToday(todayData && typeof todayData === "object" ? todayData : emptyStats());
      setMtd(mtdData && typeof mtdData === "object" ? mtdData : emptyStats());
      setRecent(Array.isArray(leadsPage?.data) ? leadsPage.data : []);
    } catch (err) {
      console.error(err);
      setToday(null);
      setMtd(null);
      setRecent([]);
      setError(
        err?.response?.status === 403
          ? "You do not have access to Social Leads."
          : "Could not load Social Media Dashboard."
      );
    } finally {
      setLoading(false);
    }
  }, [branchParams, date]);

  useEffect(() => {
    load();
  }, [load]);

  const t = today || emptyStats();
  const m = mtd || emptyStats();

  const mediumRows = useMemo(() => {
    const map = t.by_contact_medium || {};
    const rows = Object.entries(map).map(([label, total]) => ({
      label: label || "Not set",
      total: Number(total || 0),
    }));
    rows.sort((a, b) => b.total - a.total);
    const max = rows[0]?.total || 1;
    return rows.map((r) => ({ ...r, pct: Math.round((r.total / max) * 100) }));
  }, [t.by_contact_medium]);

  const offerRows = useMemo(() => {
    const rows = Array.isArray(t.by_offer) ? [...t.by_offer] : [];
    rows.sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
    const max = Number(rows[0]?.total || 1) || 1;
    return rows.slice(0, 8).map((r) => ({
      label: r.offer || "Not set",
      total: Number(r.total || 0),
      converted: Number(r.converted || 0),
      pct: Math.round((Number(r.total || 0) / max) * 100),
    }));
  }, [t.by_offer]);

  const quickLinks = useMemo(
    () => [
      { to: "/social-leads", icon: "bi-people", label: "Social Leads" },
      { to: "/social-leads", icon: "bi-plus-circle", label: "Add Lead" },
    ],
    []
  );

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design sm-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="sm-topbar">
              <div>
                <p className="sm-eyebrow">Social Media</p>
                <h2 className="sm-title">
                  <i className="bi bi-megaphone me-2"></i>
                  Social Media Dashboard
                </h2>
                <p className="sm-subtitle">
                  {moment(date).format("dddd, DD MMM YYYY")}
                  {first_name
                    ? ` · Welcome ${first_name}${last_name ? ` ${last_name}` : ""}`
                    : ""}
                  {` · ${branchLabel(branch)}`}
                </p>
              </div>
              <div className="sm-toolbar">
                <DashboardBranchFilter
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                />
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="Date"
                />
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={load}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !today ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading social media dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="sm-section-title">Your workspace</h3>
                <div className="sm-links">
                  {quickLinks.map((q) => (
                    <Link key={q.label} to={q.to} className="sm-link">
                      <i className={`bi ${q.icon}`}></i>
                      {q.label}
                    </Link>
                  ))}
                </div>

                <h3 className="sm-section-title">Today at a glance</h3>
                <div className="sm-kpi-grid">
                  <Kpi
                    label="Leads Today"
                    value={num(t.total_leads)}
                    sub={`WhatsApp detail ${num(t.whatsapp_detail)}`}
                    icon="bi-inbox"
                    tone="teal"
                  />
                  <Kpi
                    label="Interested"
                    value={num(t.interested)}
                    sub={`Not interested ${num(t.not_interested)}`}
                    icon="bi-hand-thumbs-up"
                    tone="blue"
                  />
                  <Kpi
                    label="Visits Scheduled"
                    value={num(t.visit_scheduled)}
                    sub={`Completed ${num(t.visit_completed)}`}
                    icon="bi-calendar-check"
                    tone="amber"
                  />
                  <Kpi
                    label="Payments"
                    value={num(t.payment_received)}
                    sub={`Conversion ${num(t.conversion_rate)}%`}
                    icon="bi-cash-coin"
                    tone="rose"
                  />
                  <Kpi
                    label="Calls Answered"
                    value={num(t.first_call_answered)}
                    sub="First call answered"
                    icon="bi-telephone"
                    tone="violet"
                  />
                  <Kpi
                    label="Not Answered"
                    value={num(t.call_not_answered)}
                    sub="Called, nobody picked up"
                    icon="bi-telephone-x"
                    tone="amber"
                  />
                  <Kpi
                    label="WhatsApp Message"
                    value={num(t.whatsapp_message_dropped)}
                    sub="Message dropped on WhatsApp"
                    icon="bi-whatsapp"
                    tone="blue"
                  />
                  <Kpi
                    label="Month to Date"
                    value={num(m.total_leads)}
                    sub={`${num(m.payment_received)} paid · ${num(m.conversion_rate)}%`}
                    icon="bi-bar-chart"
                    tone="slate"
                  />
                </div>

                <div className="sm-card">
                  <h3>Lead funnel (selected day)</h3>
                  <div className="sm-funnel">
                    <div className="sm-funnel-item">
                      <span>Leads</span>
                      <strong>{num(t.total_leads)}</strong>
                    </div>
                    <div className="sm-funnel-item">
                      <span>Not answered</span>
                      <strong>{num(t.call_not_answered)}</strong>
                    </div>
                    <div className="sm-funnel-item">
                      <span>WhatsApp</span>
                      <strong>{num(t.whatsapp_message_dropped)}</strong>
                    </div>
                    <div className="sm-funnel-item">
                      <span>Interested</span>
                      <strong>{num(t.interested)}</strong>
                    </div>
                    <div className="sm-funnel-item">
                      <span>Visit done</span>
                      <strong>{num(t.visit_completed)}</strong>
                    </div>
                    <div className="sm-funnel-item">
                      <span>Paid</span>
                      <strong>{num(t.payment_received)}</strong>
                    </div>
                  </div>
                </div>

                <div className="sm-grid-2">
                  <div className="sm-card" style={{ marginBottom: 0 }}>
                    <h3>By contact medium</h3>
                    {mediumRows.length === 0 ? (
                      <div className="sm-empty">No leads for this day.</div>
                    ) : (
                      mediumRows.map((r) => (
                        <div className="sm-bar-row" key={r.label}>
                          <div className="sm-bar-label" title={r.label}>
                            {r.label}
                          </div>
                          <div className="sm-bar-track">
                            <div className="sm-bar-fill" style={{ width: `${r.pct}%` }} />
                          </div>
                          <div className="sm-bar-count">{num(r.total)}</div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="sm-card" style={{ marginBottom: 0 }}>
                    <h3>By offer</h3>
                    {offerRows.length === 0 ? (
                      <div className="sm-empty">No offers for this day.</div>
                    ) : (
                      offerRows.map((r) => (
                        <div className="sm-bar-row" key={r.label}>
                          <div className="sm-bar-label" title={r.label}>
                            {r.label}
                          </div>
                          <div className="sm-bar-track">
                            <div
                              className="sm-bar-fill"
                              style={{
                                width: `${r.pct}%`,
                                background: "linear-gradient(90deg, #2563eb, #60a5fa)",
                              }}
                            />
                          </div>
                          <div className="sm-bar-count">{num(r.total)}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="sm-card">
                  <h3>This month</h3>
                  <div className="sm-mtd-row">
                    <div className="sm-mtd-item">
                      <span>Leads</span>
                      <strong>{num(m.total_leads)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>Not answered</span>
                      <strong>{num(m.call_not_answered)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>WhatsApp</span>
                      <strong>{num(m.whatsapp_message_dropped)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>Interested</span>
                      <strong>{num(m.interested)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>Visits</span>
                      <strong>{num(m.visit_completed)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>Payments</span>
                      <strong>{num(m.payment_received)}</strong>
                    </div>
                    <div className="sm-mtd-item">
                      <span>Conversion</span>
                      <strong>{num(m.conversion_rate)}%</strong>
                    </div>
                  </div>
                </div>

                <div className="sm-card">
                  <h3>Recent leads (selected day)</h3>
                  {recent.length === 0 ? (
                    <div className="sm-empty">No leads logged for this day.</div>
                  ) : (
                    <div className="sm-table-wrap">
                      <table className="sm-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Customer</th>
                            <th>Branch</th>
                            <th>Medium</th>
                            <th>Offer</th>
                            <th>Interested</th>
                            <th>Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recent.map((lead) => (
                            <tr key={lead.id}>
                              <td>
                                <Link to="/social-leads">{lead.lead_code || `#${lead.id}`}</Link>
                              </td>
                              <td>{lead.customer_name || "—"}</td>
                              <td>{lead.branch?.name || "—"}</td>
                              <td>
                                <span className="sm-pill">{lead.contact_medium || "—"}</span>
                              </td>
                              <td>{lead.offer || "—"}</td>
                              <td>
                                <span
                                  className={`sm-pill ${
                                    lead.interested ? "sm-pill-yes" : "sm-pill-no"
                                  }`}
                                >
                                  {lead.interested ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`sm-pill ${
                                    lead.payment_received ? "sm-pill-yes" : "sm-pill-no"
                                  }`}
                                >
                                  {lead.payment_received ? "Yes" : "No"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mt-3">
                    <Link to="/social-leads" className="btn btn-sm btn-outline-danger">
                      Open Social Leads
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
